import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useGetLoginOptions } from 'features/auth/hooks/use-auth';
import { useAuthState } from 'state/client-middleware';
import { GeometricAnimation } from 'components/ui/geometric-animation/GeometricAnimation';
import { KlaxAuthTopBar } from './shared/klax-auth-top-bar';
import { AuthPanelLoader } from './shared/auth-panel-loader';

type HttpError = {
  message?: string;
  status?: number;
  response?: {
    status?: number;
  };
};

function isClientConfigError(error: HttpError | null | undefined): boolean {
  if (!error) return false;

  const configErrorStatuses = [403, 404, 406, 424];
  const status = error.response?.status ?? error.status;

  if (status && configErrorStatuses.includes(status)) {
    return true;
  }

  if (error.message) {
    return configErrorStatuses.some((code) => error.message?.includes(`HTTP ${code}`));
  }

  return false;
}

function isServerError(error: HttpError | null | undefined): boolean {
  if (!error) return false;

  const status = error.response?.status ?? error.status;
  if (status && status >= 500 && status < 600) {
    return true;
  }

  if (error.message) {
    const httpMatch = error.message.match(/HTTP (\d{3})/);
    if (httpMatch) {
      const statusFromMessage = parseInt(httpMatch[1], 10);
      return statusFromMessage >= 500 && statusFromMessage < 600;
    }
  }

  return false;
}

export function AuthLayout() {
  const { isLoading, error: loginOptionsError } = useGetLoginOptions();
  const navigate = useNavigate();
  const { isMounted, isAuthenticated, isMfaEnabled } = useAuthState();
  useEffect(() => {
    if (
      isAuthenticated &&
      !isMfaEnabled &&
      !window.location.pathname.includes('/verify-key')
    ) {
      navigate('/');
    }
  }, [isAuthenticated, isMfaEnabled, navigate]);

  const isAuthShellLoading = !isMounted || isLoading;

  const renderAuthContent = () => {
    if (isAuthShellLoading) {
      return <AuthPanelLoader />;
    }

    if (isClientConfigError(loginOptionsError as HttpError | null | undefined)) {
      return (
        <div className="mx-auto w-full max-w-xl">
          <div className="relative overflow-hidden rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 p-8 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/80 to-transparent" />
            <div className="relative z-10">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-red-900">
                  Incorrect Project Key
                </h2>
                <div className="space-y-3 text-red-700">
                  <p className="text-base leading-relaxed">
                    It seems your project is not set up in the Blocks Cloud.
                  </p>
                  <p className="text-sm leading-relaxed">
                    Please create a project at{' '}
                    <a
                      href="https://cloud.seliseblocks.com"
                      className="font-semibold underline decoration-red-400 underline-offset-2 hover:decoration-red-600"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      cloud.seliseblocks.com
                    </a>
                    , then update your{' '}
                    <code className="inline-flex items-center rounded-md border border-red-300/50 bg-red-200/60 px-2 py-1 font-mono text-xs text-red-800">
                      .env
                    </code>{' '}
                    configuration accordingly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isServerError(loginOptionsError as HttpError | null | undefined)) {
      return (
        <div className="mx-auto w-full max-w-xl">
          <div className="relative overflow-hidden rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100/50 p-8 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/80 to-transparent" />
            <div className="relative z-10">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-orange-100 p-3">
                  <AlertTriangle className="h-8 w-8 text-orange-600" />
                </div>
              </div>
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-orange-900">
                  Services Temporarily Unavailable
                </h2>
                <div className="space-y-3 text-orange-700">
                  <p className="text-base leading-relaxed">
                    The services are temporarily unavailable.
                  </p>
                  <p className="text-base font-semibold leading-relaxed">
                    Everything will be back to normal soon.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return <Outlet />;
  };

  return (
    <div className="relative h-screen w-full overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <GeometricAnimation className="h-full w-full" />
      </div>

      <div className="relative z-10 flex h-full w-full items-center justify-center overflow-y-auto px-4 py-16 sm:px-6">
        <div className="absolute right-3 top-3 z-10 max-w-[calc(100%-1.5rem)] sm:right-6 sm:top-6 sm:max-w-none">
          <KlaxAuthTopBar />
        </div>
        <div className="w-full max-w-[520px] rounded-2xl bg-white px-6 py-10 shadow-lg sm:px-10">
          {renderAuthContent()}
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
